
  import { computed, defineComponent, PropType, toRefs } from 'vue';
  import { InputField, CheckboxField, SwitchField } from '@/ui/components/form';
  import { Field } from '@/ui/components/form/Field';
  import PrioritySelector from '@/_legacy/components/Issues/PrioritySelector.vue';
  import { DueAfterSelection } from '@/ui/components/issueTemplate/issueCreateTemplateModal/DueAfterSelection';
  import ScoreTriggerOptions from '@/ui/components/issueTemplate/issueCreateTemplateModal/ScoreTriggerOptions.vue';
  import Approvals from '@/_legacy/views/ChecklistBuilder/Settings/Approvals.vue';
  import AssigneesField from '@/ui/pages/user/assignees/AssigneesField.vue';

  import { IssueTemplateEditable, emptyDueAfter, emptyScoreTrigger, ScoreTriggerEditable } from '@/ui/DTO';
  import { approvalsEnabledForOrganization, hasSomePermission } from '@/ui/auth/auth';
  import { actionApprovalsPermissions } from '@/ui/auth/auth.types';
  import { SelectOptions } from '@/ui/types';
  import isEmail from 'validator/lib/isEmail';
  import {
    filterListToEditableGroups,
    filterListToEditableUsers,
    mapGroupIdsToOptions,
    mapUsersIdsToOptions,
  } from '@/ui/helpers';
  import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';
  import { IssuePriority } from '@/entities/issue';
  import { DueAfter } from '@/entities/issueTemplate';
  import { ValidationErrors } from '@/entities/validation';

  export default defineComponent({
    name: 'IssueTemplateForm',
    components: {
      InputField,
      CheckboxField,
      SwitchField,
      Field,
      PrioritySelector,
      DueAfterSelection,
      ScoreTriggerOptions,
      Approvals,
      AssigneesField,
    },
    props: {
      record: { type: Object as () => IssueTemplateEditable, required: true },
      userOptions: { type: Array as () => SelectOptions, required: true },
      userApprovalOptions: { type: Array as () => SelectOptions<string>, required: true },
      validationErrors: { type: Array as PropType<ValidationErrors>, required: true },
    },
    emits: ['update-user-options'],
    setup(props, { emit }) {
      const { record } = toRefs(props);

      const showDueAfter = computed(() => record.value.dueIn !== null);
      const showScoreTrigger = computed(() => record.value.scoreTrigger !== null);
      const canSeeApprovals = computed(
        () => approvalsEnabledForOrganization.value && hasSomePermission(actionApprovalsPermissions),
      );

      const toggleDueAfter = (v: boolean) => {
        record.value.dueIn = v ? emptyDueAfter() : null;
      };

      const toggleScoreTrigger = (v: boolean) => {
        record.value.scoreTrigger = v ? emptyScoreTrigger() : null;
      };

      const extractUserValue = (value: string | number) => value.toString().replace('user_', '');

      const onAssigneesChange = (value: Array<string | number>) => {
        const assignees: string[] = [];
        const associateAssignees: string[] = [];
        const groups: string[] = [];

        value.forEach(v => {
          const _value = extractUserValue(v);
          const s = String(v);

          if (isEmail(_value)) {
            associateAssignees.push(_value);
          } else if (s.includes('user_')) {
            assignees.push(s);
          } else if (s.includes('group_')) {
            groups.push(s);
          }
        });

        record.value.assignees = filterListToEditableUsers(assignees).map(u => Number(u));
        record.value.assignedGroups = filterListToEditableGroups(groups).map(u => Number(u));
        record.value.associateAssignees = associateAssignees;
      };

      const identifier = CreateUserModelIdentifier.issueBuilderApprovers;

      const updateUserOptions = () => {
        emit('update-user-options');
      };

      const updateTitle = (value: string) => {
        record.value.title = value;
      };

      const updateDescription = (value: string) => {
        record.value.description = value;
      };

      const updateEntityItemSpecificAssignments = (value: boolean) => {
        record.value.entityItemSpecificAssignments = value;
      };

      const updatePriority = (value: IssuePriority) => {
        record.value.priority = value;
      };

      const updateDueIn = (value: DueAfter) => {
        record.value.dueIn = value;
      };

      const updateScoreTrigger = (value: ScoreTriggerEditable) => {
        record.value.scoreTrigger = value;
      };

      const updateApprovalsEnabled = (value: boolean) => {
        record.value.approvalsEnabled = value;
      };

      const updateEntityRestrictionsEnabled = (value: boolean) => {
        record.value.approvalsEntitySpecificAssignments = value;
      };

      // @ts-ignore
      const updateChecklistApprovers = (value: any) => {
        record.value.approvers = value;
      };

      return {
        showDueAfter,
        showScoreTrigger,
        canSeeApprovals,
        identifier,
        toggleDueAfter,
        toggleScoreTrigger,
        onAssigneesChange,
        mapUsersIdsToOptions,
        mapGroupIdsToOptions,
        updateUserOptions,
        updateTitle,
        updateDescription,
        updateEntityItemSpecificAssignments,
        updatePriority,
        updateDueIn,
        updateScoreTrigger,
        updateApprovalsEnabled,
        updateEntityRestrictionsEnabled,
        updateChecklistApprovers,
      };
    },
  });
